import { createContext, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import classes from "./NewFileUploadLayout.module.css";
import { Icon, Notifications, Spinner, Text } from "@jds/core";
import { v4 as uuidv4 } from "uuid";

import {
  addParamsURL,
  bytesToHumanReadable,
  getChunkSizeByBandWidth,
  getDynamicChunkSize,
  getFileMD5,
  validateFileSignature,
} from "../../../util/helper";
import decryptUrl from "../../../util/img-decryption";
import ImageCard from "../../UI/ImageCard/ImageCard";
import JdsModal from "../../UI/JdsModal";
import useApiHttp from "../../../hooks/use-http";
import {
  setActiveModalClose,
  setActiveMsg,
} from "../../../store/state-slices/plans";
import { useDispatch } from "react-redux";
import SparkMD5 from "spark-md5";
import { photoUploadTrigger } from "../../../store/state-slices/apiTrigger";
import { uploadPhotosActions } from "../../../store/state-slices/photosUpload";
import FAQ from "../../FAQ/FAQ";
import FileDetailsSidebar from "../../FileDetailsSidebar/FileDetailsSidebar";
export const NewFileUploadContext = createContext(null);

export default function NewFileUploadLayout() {
  const [queue, setQueue] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [showUploadCon, setShowUploadCon] = useState(true);
  const [showTaskCon, setShowTaskCon] = useState(false);
  const [cancelAllTaskModalCls, setCancelAllTaskModalCls] = useState(true);
  const [cancelFolTaskModalCls, setCancelFolTaskModalCls] = useState(true);
  const [cancelFolTasDetail, setCancelFolTasDetail] = useState({});
  const currentAbort = useRef(null);
  const activeUploads = useRef(0);

  const DEFAULT_CONCURRENCY = 4;
  const MIN_CONCURRENCY = 1;
  const ENABLE_DYNAMIC_CONCURRENCY = true;

  const maxConcurrencyRef = useRef(DEFAULT_CONCURRENCY);
  const chunkTimesRef = useRef([]);

  const { sendRequest: uploadImgRequest } = useApiHttp();
  const { sendRequest: chunkInitiateRequest } = useApiHttp();
  const { sendRequest: chunkUploadRequest } = useApiHttp();

  const dispatch = useDispatch();

  const withTimeout = (promise, ms = 30000) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(
          () =>
            reject({
              data: { message: "Request timeout" },
              wasAborted: false,
              timeOut: true,
            }),
          ms,
        ),
      ),
    ]);

  const adjustConcurrency = () => {
    console.log("adjustConcurrency CALLED ===========");
    if (!ENABLE_DYNAMIC_CONCURRENCY) return;

    const times = chunkTimesRef.current;
    console.log("chunkTIMEs:", times);
    if (!times.length) return;

    const avg = times.reduce((a, b) => a + b, 0) / times.length;

    const prevLimit = maxConcurrencyRef.current;

    // 🎯 Updated realistic threshold
    let newLimit = avg > 25000 ? 1 : 4;

    maxConcurrencyRef.current = newLimit;

    console.log("\n------------------------------");
    console.log("📊 CONCURRENCY (Real-world tuned)");
    console.log(`Avg Chunk Time: ${avg.toFixed(0)} ms`);
    console.log(`Concurrency: ${prevLimit} → ${newLimit}`);
    console.log("👉 maxConcurrencyRef.current:", maxConcurrencyRef.current);
    console.log(`Active: ${activeUploads.current}`);
    console.log("------------------------------\n");

    if (prevLimit !== newLimit) {
      console.log("⚡ CONCURRENCY LIMIT CHANGED!");
    }
  };

  const handleFileUpload = (
    files,
    eventId,
    folderId,
    folderName,
    role = "ADMIN",
  ) => {
    dispatch(photoUploadTrigger({ id: eventId + folderId, value: false }));
    maxConcurrencyRef.current = 4;
    chunkTimesRef.current = [];
    const fileObjs = Array.from(files).map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      name: file.name,
      size: file.size,
      status: "Queued",
      filterStatus: "Queued",
    }));

    const taskIndex = tasks.findIndex(
      (t) => t.folderId === folderId && t.eventId === eventId,
    );

    if (taskIndex >= 0) {
      // Update existing task
      const updatedTasks = [...tasks];
      if (updatedTasks?.[taskIndex]?.totalFiles) {
        updatedTasks[taskIndex].totalFiles += fileObjs.length;
        // updatedTasks[taskIndex].tabFilterStatus = null;
      }

      updatedTasks?.[taskIndex]?.files?.push(...fileObjs);
      setTasks(updatedTasks);
    } else {
      // Add new task
      const newTask = {
        folderName,
        folderId,
        eventId,
        totalFiles: files.length,
        uploadComplete: 0,
        successCount: 0,
        errorCount: 0,
        cancelCount: 0,
        files: fileObjs,
        role,
        tabFilterStatus: null,
      };
      setTasks((prev) => [...prev, newTask]);
    }

    // Add files to upload queue
    setQueue((prev) => [
      ...prev,
      ...fileObjs.map((f) => ({ ...f, folderId, eventId, role })),
    ]);
  };

  const startNextUpload = () => {
    setQueue((prevQueue) => {
      let updatedQueue = [...prevQueue];

      while (
        updatedQueue.length > 0 &&
        activeUploads.current < maxConcurrencyRef.current
      ) {
        const next = updatedQueue.shift();

        activeUploads.current++;
        processFile(next);
      }

      console.log(
        `🚀 Scheduler → Active: ${activeUploads.current}/${maxConcurrencyRef.current} | Queue: ${updatedQueue.length}`,
      );

      return updatedQueue;
    });
  };

  const processFile = (next) => {
    const abortController = new AbortController();
    currentAbort.current = { id: next.id, controller: abortController };

    setTasks((prev) =>
      prev.map((task) =>
        task.folderId === next.folderId && task.eventId === next.eventId
          ? {
              ...task,
              files: task.files.map((f) =>
                f.id === next.id ? { ...f, status: "Uploading" } : f,
              ),
            }
          : task,
      ),
    );

    const uploadFn = () =>
      // withTimeout(
      //   uploadInChunks(
      //     next.file,
      //     next.eventId,
      //     next.folderId,
      //     abortController.signal,
      //     next.role,
      //   ),
      //   7 * 60 * 1000,
      uploadInChunks(
        next.file,
        next.eventId,
        next.folderId,
        abortController.signal,
        next.role,
      );
    const startTime = new Date();

    console.group("\n📤 FILE UPLOAD START");
    console.log(`📁 ${next.name}`);
    console.log(`Start Time: ${startTime.toLocaleString()}`);
    console.log(
      `Queue: ${queue.length} | Active: ${activeUploads.current}/${maxConcurrencyRef.current}`,
    );
    console.groupEnd();

    uploadFn()
      .then((data) => {
        setTasks((prev) =>
          prev.map((task) => {
            if (
              task.folderId === next.folderId &&
              task.eventId === next.eventId
            ) {
              const updatedTask = {
                ...task,
                files: task.files.map((f) =>
                  f.id === next.id
                    ? {
                        ...f,
                        status: "Uploaded",
                        filterStatus: "Uploaded",
                        response: data,
                      }
                    : f,
                ),
                uploadComplete: task.uploadComplete + 1,
                successCount: task.successCount + 1,
              };

              // 👇 TRIGGER WHEN FOLDER COMPLETES
              if (updatedTask.uploadComplete === updatedTask.totalFiles) {
                dispatch(
                  photoUploadTrigger({
                    id: updatedTask.eventId + updatedTask.folderId,
                    value: true,
                  }),
                );
              }

              return updatedTask;
            }
            return task;
          }),
        );

        const endTime = new Date();

        console.group("\n✅ FILE UPLOAD SUCCESS");
        console.log(`📁 ${next.name}`);
        console.log(`Start: ${startTime.toLocaleString()}`);
        console.log(`End: ${endTime.toLocaleString()}`);
        console.log(`Duration: ${endTime - startTime} ms`);
        console.log(
          `Queue: ${queue.length} | Active: ${activeUploads.current}/${maxConcurrencyRef.current}`,
        );
        console.groupEnd();
      })
      .catch(({ data, wasAborted }) => {
        setTasks((prev) =>
          prev.map((task) => {
            if (
              task.folderId === next.folderId &&
              task.eventId === next.eventId
            ) {
              // return {
              //   ...task,
              //   files: task.files.map(f =>
              //     f.id === next.id
              //       ? {
              //         ...f,
              //         status: wasAborted ? "Canceled" : "Error",
              //         error: data?.message || "Upload failed"
              //       }
              //       : f
              //   ),
              //   uploadComplete: task.uploadComplete + 1,
              //   errorCount: wasAborted
              //     ? task.errorCount
              //     : task.errorCount + 1,
              //   cancelCount: wasAborted
              //     ? task.cancelCount + 1
              //     : task.cancelCount
              // };
              const updatedTask = {
                ...task,
                files: task.files.map((f) =>
                  f.id === next.id
                    ? {
                        ...f,
                        status: wasAborted ? "Canceled" : "Error",
                        filterStatus: wasAborted ? "Canceled" : "Error",
                        error: data?.message || "Upload failed",
                      }
                    : f,
                ),
                uploadComplete: task.uploadComplete + 1,
                errorCount: wasAborted ? task.errorCount : task.errorCount + 1,
                cancelCount: wasAborted
                  ? task.cancelCount + 1
                  : task.cancelCount,
              };

              // 👇 TRIGGER WHEN FOLDER COMPLETES
              if (updatedTask.uploadComplete === updatedTask.totalFiles) {
                dispatch(
                  photoUploadTrigger({
                    id: updatedTask.eventId + updatedTask.folderId,
                    value: true,
                  }),
                );
              }

              return updatedTask;
            }
            return task;
          }),
        );

        const endTime = new Date();

        console.group("\n❌ FILE UPLOAD FAILED");
        console.log(`📁 ${next.name}`);
        console.log(`Start: ${startTime.toLocaleString()}`);
        console.log(`End: ${endTime.toISOString()}`);
        console.log(`Duration: ${endTime - startTime} ms`);
        console.log(`Reason: ${wasAborted ? "Canceled" : data?.message}`);
        console.log(
          `Queue: ${queue.length} | Active: ${activeUploads.current}/${maxConcurrencyRef.current}`,
        );
        console.groupEnd();
      })
      .finally(() => {
        console.log(
          `🔁 AFTER COMPLETE → Active: ${activeUploads.current - 1}/${maxConcurrencyRef.current}`,
        );
        activeUploads.current--;
        currentAbort.current = null;
        startNextUpload(); // 👈 VERY IMPORTANT
      });
  };

  // const uploadNextFile = async () => {
  //   if (queue.length === 0) return;
  //   if (activeUploads.current >= maxConcurrencyRef.current) return;

  //   const next = queue[0];
  //   setQueue(prev => prev.slice(1));

  //   activeUploads.current++;

  //   const abortController = new AbortController();
  //   currentAbort.current = { id: next.id, controller: abortController };

  //   // Mark file as "Uploading"
  //   setTasks((prev) =>
  //     prev.map((task) =>
  //       task.folderId === next.folderId && task.eventId === next.eventId
  //         ? {
  //           ...task,
  //           files: task.files.map((f) =>
  //             f.id === next.id ? { ...f, status: "Uploading" } : f,
  //           ),
  //         }
  //         : task,
  //     ),
  //   );

  //   const formData = new FormData();
  //   const file = next.file;
  //   if (next.role == "ADMIN") {
  //     formData.append("uploadFile", file);
  //   } else {
  //     formData.append("uploadFiles", file); // for photographer
  //   }
  //   formData.append("eventId", next.eventId);
  //   formData.append("folderId", next.folderId);

  //   const isChunkUpload = file.size > 6 * 1024 * 1024;

  //   // const uploadFn = isChunkUpload
  //   //   ? () =>
  //   //     uploadInChunks(
  //   //       file,
  //   //       next?.eventId,
  //   //       next?.folderId,
  //   //       abortController?.signal,
  //   //       next.role
  //   //     )
  //   // : () => singleFileUpload(formData, abortController, next.role);

  //   // const uploadFn = () =>
  //   //   uploadInChunks(
  //   //     file,
  //   //     next?.eventId,
  //   //     next?.folderId,
  //   //     abortController?.signal,
  //   //     next.role
  //   //   );

  //   const uploadFn = () =>
  //     withTimeout(
  //       uploadInChunks(
  //         file,
  //         next?.eventId,
  //         next?.folderId,
  //         abortController?.signal,
  //         next.role,
  //       ),
  //       7 * 60 * 1000,
  //     );

  //   let attempt = 0;
  //   const maxRetries = 1;

  //   const uploadWithRetry = async () => {
  //     return uploadFn().catch((err) => {
  //       console.log("Hello1234==timeout==>", err);

  //       if (
  //         attempt < maxRetries &&
  //         !err?.wasAborted &&
  //         !err?.timeOut &&
  //         err?.data?.statusCode !== 1001 &&
  //         err?.data?.statusCode !== 1002 &&
  //         err?.data?.statusCode !== 400 &&
  //         err?.data?.statusCode !== 403
  //       ) {
  //         attempt++;
  //         console.warn(`Retrying1 ${next.name}, attempt ${attempt} `);
  //         console.log(`Retrying1`, err);
  //         return uploadFn();
  //       }
  //       if (err.timeOut) {
  //         currentAbort?.current?.controller?.abort("TIME_OUT");
  //       }
  //       throw err;
  //     });
  //   };

  //   console.time(`Upload ${next.name}`);

  //   validateFileSignature(file, {
  //     jpeg: ["ffd8ffe0", "ffd8ffe1", "ffd8ffe2", "ffd8ffdb"], // JPEG signatures can vary
  //     png: ["89504e47"],
  //   })
  //     .then((isValid) => {
  //       if (isValid) {
  //         uploadWithRetry()
  //           .then((data) => {
  //             console.timeEnd(`Upload ${next.name}`);

  //             setTasks((prev) => {
  //               return prev.map((task) => {
  //                 if (
  //                   task.folderId === next.folderId &&
  //                   task.eventId === next.eventId
  //                 ) {
  //                   const updatedTask = {
  //                     ...task,
  //                     files: task.files.map((f) =>
  //                       f.id === next.id
  //                         ? { ...f, status: "Uploaded", response: data }
  //                         : f,
  //                     ),
  //                     uploadComplete: task.uploadComplete + 1,
  //                     successCount: task.successCount + 1,
  //                   };
  //                   if (updatedTask.uploadComplete === updatedTask.totalFiles) {
  //                     dispatch(
  //                       photoUploadTrigger({
  //                         id: updatedTask.eventId + updatedTask.folderId,
  //                         value: true,
  //                       }),
  //                     );
  //                   }
  //                   return updatedTask;
  //                 }
  //                 return task;
  //               });
  //             });
  //           })
  //           .catch(({ err, data, wasAborted }) => {
  //             console.log(
  //               "catch function inside uploadwithretry",
  //               err + " | ",
  //               data,
  //             );
  //             console.log("Hello1234==>retry==>", { err, data, wasAborted });

  //             if (data?.statusCode === 403 || data?.statusCode === 402) {
  //               dispatch(setActiveModalClose(false));
  //               dispatch(setActiveMsg(data?.errors?.planType));
  //               cancelAllTask();
  //             } else {
  //               console.error(
  //                 `Upload failed for ${next.name}:`,
  //                 data?.message || err,
  //               );
  //               setTasks((prev) =>
  //                 prev.map((task) => {
  //                   if (
  //                     task.folderId === next.folderId &&
  //                     task.eventId === next.eventId
  //                   ) {
  //                     const updateTask = {
  //                       ...task,
  //                       files: task.files.map((f) =>
  //                         f.id === next.id
  //                           ? {
  //                             ...f,
  //                             status: wasAborted ? "Canceled" : "Error",
  //                             error: data?.message || "Unknown error",
  //                             responce: data || null,
  //                             statusCode: data?.statusCode || null,
  //                           }
  //                           : f,
  //                       ),
  //                       uploadComplete: task.uploadComplete + 1,
  //                       cancelCount: wasAborted
  //                         ? task.cancelCount + 1
  //                         : task.cancelCount,
  //                       errorCount: wasAborted
  //                         ? task.errorCount
  //                         : task.errorCount + 1,
  //                     };
  //                     if (updateTask.uploadComplete === updateTask.totalFiles) {
  //                       dispatch(
  //                         photoUploadTrigger({
  //                           id: updateTask.eventId + updateTask.folderId,
  //                           value: true,
  //                         }),
  //                       );
  //                     }
  //                     return updateTask;
  //                   }
  //                   return task;
  //                 }),
  //               );
  //             }
  //           })
  //           .finally(() => {
  //             console.log("Retry filanny");

  //             setQueue((prev) =>
  //               prev.filter((queueItem) => queueItem.id !== next.id),
  //             );
  //             // isUploading.current = false;
  //             activeUploads.current--;
  //             currentAbort.current = null;
  //           });
  //       } else {
  //         setTasks((prev) =>
  //           prev.map((task) => {
  //             if (
  //               task.folderId === next.folderId &&
  //               task.eventId === next.eventId
  //             ) {
  //               const updateTask = {
  //                 ...task,
  //                 files: task.files.map((f) =>
  //                   f.id === next.id
  //                     ? {
  //                       ...f,
  //                       status: "Error",
  //                       error:
  //                         "Upload a valid image file (JPG, JPEG and PNG).",
  //                       responce: null,
  //                       statusCode: null,
  //                     }
  //                     : f,
  //                 ),
  //                 uploadComplete: task.uploadComplete + 1,

  //                 errorCount: task.errorCount + 1,
  //               };
  //               if (updateTask.uploadComplete === updateTask.totalFiles) {
  //                 dispatch(
  //                   photoUploadTrigger({
  //                     id: updateTask.eventId + updateTask.folderId,
  //                     value: true,
  //                   }),
  //                 );
  //               }
  //               return updateTask;
  //             }
  //             return task;
  //           }),
  //         );
  //         setQueue((prev) =>
  //           prev.filter((queueItem) => queueItem.id !== next.id),
  //         );
  //         // isUploading.current = false;
  //         currentAbort.current = null;
  //       }
  //     })
  //     .catch((error) => {
  //       setTasks((prev) =>
  //         prev.map((task) => {
  //           if (
  //             task.folderId === next.folderId &&
  //             task.eventId === next.eventId
  //           ) {
  //             const updateTask = {
  //               ...task,
  //               files: task.files.map((f) =>
  //                 f.id === next.id
  //                   ? {
  //                     ...f,
  //                     status: "Error",
  //                     error: "Upload a valid image file (JPG, JPEG and PNG).",
  //                     responce: null,
  //                     statusCode: null,
  //                   }
  //                   : f,
  //               ),
  //               uploadComplete: task.uploadComplete + 1,

  //               errorCount: task.errorCount + 1,
  //             };
  //             if (updateTask.uploadComplete === updateTask.totalFiles) {
  //               dispatch(
  //                 photoUploadTrigger({
  //                   id: updateTask.eventId + updateTask.folderId,
  //                   value: true,
  //                 }),
  //               );
  //             }
  //             return updateTask;
  //           }
  //           return task;
  //         }),
  //       );
  //       setQueue((prev) =>
  //         prev.filter((queueItem) => queueItem.id !== next.id),
  //       );
  //       // isUploading.current = false;
  //       currentAbort.current = null;
  //     });
  // };

  const singleFileUpload = (formData, signal, role) => {
    const controller = signal || new AbortController();
    const { signal: fetchSignal } = controller;

    const singleFileUploadPromise = new Promise((res, rej) => {
      const startTime = new Date();
      console.log(
        `[UPLOAD START] ${
          formData.get("uploadFile")?.name ||
          formData.get("uploadFiles")?.name ||
          "unknown"
        } at ${startTime.toISOString()}`,
      );

      uploadImgRequest(
        {
          url: `${
            role === "ADMIN" ? "uploadMedia" : "photographer/uploadMedia"
          }`,
          method: "POST",
          body: formData,
          formData: true,
          base: "MEDIA",
          signal: fetchSignal,
          noRefresh: true,
          requestId: uuidv4(),
        },
        null,
        (data) => {
          const endTime = new Date();
          console.log(
            `[UPLOAD END] ${
              formData.get("uploadFile")?.name ||
              formData.get("uploadFiles")?.name ||
              "unknown"
            } at ${endTime.toISOString()} | Duration: ${
              (endTime - startTime) / 1000
            }s`,
          );
          res(data);
        },
        (err, data) => {
          const endTime = new Date();
          console.warn(
            `[UPLOAD FAILED] ${
              formData.get("uploadFile")?.name ||
              formData.get("uploadFiles")?.name ||
              "unknown"
            } at ${endTime.toISOString()} | Duration: ${
              (endTime - startTime) / 1000
            }s`,
          );
          const wasAborted = controller.signal.aborted;
          rej({ err, data, wasAborted });
          if (data?.statusCode === 404) {
            const folderId = formData.get("folderId");
            const eventId = formData.get("eventId");
            cancelFolderTask(folderId, eventId);
            Notifications.toast.error({
              id: "Folder deleted while upload",
              title: data?.message,
            });
          }
        },
      );
    });
    if (fetchSignal) {
      fetchSignal.addEventListener("abort", () => {
        controller.abort();
      });
    }
    return singleFileUploadPromise;
  };

  const uploadInChunks = async (file, eventId, folderId, signal, role) => {
    try {
      const hash = await getFileMD5(file);

      const chunkInitiatePayload = {
        name: file.name,
        size: file.size,
        hash,
        eventId,
        folderId,
        // requestId: uuidv4(),
      };
      let proceedChunk = false;

      const initData = await new Promise((resolve, reject) => {
        chunkInitiateRequest(
          {
            url: `initiate/chunk/upload/${
              role === "ADMIN" ? "organizer" : "photographer"
            }`,
            base: "MEDIA",
            method: "POST",
            body: chunkInitiatePayload,
            signal,
            noRefresh: true,
            requestId: uuidv4(),
          },
          () => {}, // optional onStart
          (data, res) => {
            if (res.status === 201) {
              // File already uploaded, skip
              resolve(data);
            } else {
              proceedChunk = true;
              resolve(data);
            }
          },
          (err, data) => {
            const wasAborted = signal?.aborted; // to count cancelled files
            reject({ err, data, wasAborted });
            if (data?.statusCode === 404) {
              // const folderId = formData.get('folderId')
              // const eventId = formData.get('eventId')
              cancelFolderTask(folderId, eventId);
              Notifications.toast.error({
                id: "Folder deleted while upload",
                title: data?.message,
              });
            }
          },
        );
      });

      // If already uploaded
      if (!proceedChunk) {
        console.log("⚠️ Skipping chunk upload → running concurrency check");
        adjustConcurrency();
        return initData;
      }

      const { transactionId: uploadId, offset = 0 } = initData.data;
      // const chunkSize = getDynamicChunkSize(file.size);
      let chunkSize = 6 * 1024 * 1024;
      let currentOffset = offset;

      while (currentOffset < file.size) {
        const chunk = file.slice(currentOffset, currentOffset + chunkSize);
        const chunkBuffer = await chunk.arrayBuffer();
        const chunkHash = SparkMD5.ArrayBuffer.hash(chunkBuffer);
        let chunkSuccessData = false;
        const formData = new FormData();
        formData.append("chunkFile", new Blob([chunkBuffer]), file.name);
        formData.append("uploadId", uploadId);
        formData.append("chunkLength", chunk.size);
        formData.append("chunkHash", chunkHash);
        formData.append("offset", currentOffset);
        formData.append("eventId", eventId);
        formData.append("fileName", file.name);
        formData.append("folderId", folderId);

        let headers = {
          "Content-Type": "application/octet-stream",
          "x-file-name": file.name,
          "x-upload-id": uploadId,
          "x-chunk-length": chunk.size,
          "x-chunk-hash": chunkHash,
          "x-offset": currentOffset,
          "x-event-id": eventId,
          "x-folder-id": folderId,
        };

        let startTime = Date.now();
        await new Promise((resolve, reject) => {
          chunkUploadRequest(
            {
              url: `chunk/upload/${
                role === "ADMIN" ? "organizer" : "photographer"
              }`,
              base: "MEDIA",
              method: "POST",
              // headers,
              formData: true,
              body: formData,
              // binary: true,
              // body: new Blob([chunkBuffer]),
              signal,
              noRefresh: true,
              requestId: uuidv4(),
            },
            () => {},
            (data, res) => {
              if (res.status === 201) {
                resolve(data);
                chunkSuccessData = data;
              } else {
                resolve();
              }
            },
            (err, data) => {
              const wasAborted = signal?.aborted;
              reject({ err, data, wasAborted });
              if (data?.statusCode === 404) {
                // const folderId = formData.get('folderId')
                // const eventId = formData.get('eventId')
                cancelFolderTask(folderId, eventId);
                Notifications.toast.error({
                  id: "Folder deleted while upload",
                  title: data?.message,
                });
              }
            },
          );
        });
        if (chunkSuccessData) {
          return chunkSuccessData;
        }
        currentOffset += chunkSize;
        // console.log("chunk start time", startTime);
        let endTime = Date.now();
        const duration = endTime - startTime;

        chunkTimesRef.current.push(duration);
        if (chunkTimesRef.current.length > 5) {
          chunkTimesRef.current.shift();
        }

        adjustConcurrency();

        chunkSize = getChunkSizeByBandWidth(endTime - startTime, chunkSize);
      }
    } catch (e) {
      // If aborted
      if (signal?.aborted) {
        throw {
          err: null,
          data: { message: "Aborted" },
          wasAborted: true,
        };
      }

      // If useApiHttp nested error inside e.err
      if (e?.err?.err === true) {
        throw {
          err: null,
          data: {
            statusCode: e.err.statusCode,
            message: e.err.message,
            errors: e.err.data?.errors,
          },
          wasAborted: false,
        };
      }

      // If already structured (your format)
      if (e?.err || e?.data || typeof e?.wasAborted !== "undefined") {
        throw e;
      }

      // Browser-level Error
      if (e instanceof Error) {
        throw {
          err: e,
          data: { message: e.message },
          wasAborted: false,
        };
      }

      // Generic fallback
      throw {
        err: e,
        data: e?.data || { message: "Unknown error" },
        wasAborted: false,
      };
    }
  };

  const cancelAllTask = () => {
    currentAbort?.current?.controller?.abort();
    setQueue([]);
    setTasks([]);
    setShowUploadCon(true);
    setShowTaskCon(false);
  };

  const cancelFolderTask = (folderId, eventId) => {
    currentAbort?.current?.controller?.abort();
    setQueue((pre) => {
      let updateArr = pre.filter((queue) => {
        if (queue?.folderId != folderId) {
          return true;
        }
      });

      return updateArr;
    });
    setTasks((pre) => pre.filter((task) => task?.folderId != folderId));
  };

  const cancelTask = (fileId) => {
    if (currentAbort.current?.id === fileId) {
      currentAbort.current.controller.abort();
    } else {
      setQueue((prev) => prev.filter((f) => f.id !== fileId));

      setTasks((prev) =>
        prev.map((task) => {
          let uploadCompleteStatus = false;

          let updateFiles = task.files.map((f) => {
            if (f.id === fileId) {
              uploadCompleteStatus = true;
              return { ...f, status: "Canceled", filterStatus: "Canceled" };
            } else {
              return f;
            }
          });
          return {
            ...task,
            files: updateFiles,
            uploadComplete: uploadCompleteStatus
              ? task.uploadComplete + 1
              : task.uploadComplete,
            cancelCount: uploadCompleteStatus
              ? task.cancelCount + 1
              : task.cancelCount,
          };
        }),
      );
    }
  };

  const retryTask = (fileId) => {
    setTasks((prev) =>
      prev.map((task, tIndex) => {
        let retryFile = null;
        let folderId = null;
        let eventId = null;
        let role = null;
        let errorStatus = false;
        let cancelStatus = false;
        let uploadCompleteStatus = false;
        const updatedFiles = task.files.map((f) => {
          if (f.id === fileId) {
            eventId = task?.eventId;
            folderId = task?.folderId;
            role = task?.role;
            errorStatus = f.status == "Error" ? true : false;
            cancelStatus = f.status == "Canceled" ? true : false;
            uploadCompleteStatus = true;
            retryFile = { ...f, status: "Queued" };
            setQueue((prev) => [
              ...prev,
              {
                ...retryFile,
                folderId,
                eventId,
                role,
              },
            ]);

            return retryFile;
          }
          return f;
        });

        return {
          ...task,
          files: updatedFiles,
          uploadComplete: uploadCompleteStatus
            ? task.uploadComplete - 1
            : task.uploadComplete,
          errorCount: errorStatus ? task?.errorCount - 1 : task?.errorCount,
          cancelCount: cancelStatus ? task?.cancelCount - 1 : task?.cancelCount,
          // tabFilterStatus: null,
        };
      }),
    );
  };

  const filterTask = (index, status) => {
    console.log("Hello 1234");
    setTasks((pre) =>
      pre.map((task, _innerIndex) => {
        if (_innerIndex === index) {
          return {
            ...task,
            tabFilterStatus: status,
          };
        } else {
          return task;
        }
      }),
    );
  };

  // const getFilteredFiles = (files, tabFilterStatus) => {
  //   if (!tabFilterStatus) return files;

  //   const filterMap = {
  //     succeeded: ["Queued", "Uploading", "Uploaded"],
  //     cancelled: ["Canceled"],
  //     failed: ["Error"],
  //   };
  //   const filteredFiles = files.filter((file) =>
  //     filterMap[tabFilterStatus]?.includes(file.filterStatus),
  //   );

  //   return filteredFiles.length ? filteredFiles : files;
  // };
  const getFilteredFiles = (files, tabFilterStatus) => {
    const filterMap = {
      succeeded: ["Queued", "Uploading", "Uploaded"],
      cancelled: ["Canceled"],
      failed: ["Error"],
    };

    const counts = {
      completedCount: 0,
      successCount: 0,
      errorCount: 0,
      cancelCount: 0,
      processingCount: 0,
      pendingCount: 0,
    };

    const filteredFiles = [];

    files.forEach((file) => {
      // counts
      switch (file.status) {
        case "Uploaded":
          counts.completedCount++;
          counts.successCount++;
          break;
        case "Error":
          counts.errorCount++;
          counts.completedCount++;
          break;
        case "Canceled":
          counts.cancelCount++;
          counts.completedCount++;
          break;
        case "Uploading":
          counts.processingCount++;
          break;
        case "Queued":
          counts.pendingCount++;
          break;
      }

      // filtering
      if (
        !tabFilterStatus ||
        filterMap[tabFilterStatus]?.includes(file.filterStatus)
      ) {
        filteredFiles.push(file);
      }
    });

    return {
      filteredFiles: filteredFiles.length ? filteredFiles : files,
      counts,
    };
  };

  const alertUser = (e) => {
    if (queue.length > 0) {
      e.preventDefault();
      const message = "Are you sure you want to leave this page?";
      e.returnValue = message;
      return message;
    }
  };

  useEffect(() => {
    if (activeUploads.current < maxConcurrencyRef.current && queue.length > 0) {
      startNextUpload();
    }

    dispatch(uploadPhotosActions.setTotalCount({ totalCount: queue.length }));

    window.addEventListener("beforeunload", alertUser);
    return () => window.removeEventListener("beforeunload", alertUser);
  }, [queue]);

  return (
    <>
      <NewFileUploadContext.Provider
        value={{ handleFileUpload, tasks, uploadInChunks }}>
        <Outlet />
        <FileDetailsSidebar />
        <div
          className={classes["fixed-container-section"]}
          style={{
            zIndex:
              !cancelAllTaskModalCls || !cancelFolTaskModalCls ? "60" : "61",
          }}>
          <FAQ />
          {tasks?.length > 0 && (
            <div className={classes["upload-container"]}>
              <div className={classes["upload-container-title"]}>
                <Text
                  appearance="body-xs"
                  text={
                    queue.length === 0
                      ? "Upload Complete"
                      : "Uploading in progress"
                  }
                />
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {showUploadCon ? (
                    <Icon
                      ic={"IcChevronDown"}
                      onClick={() => setShowUploadCon(false)}
                    />
                  ) : (
                    <Icon
                      ic={"IcChevronUp"}
                      onClick={() => setShowUploadCon(true)}
                    />
                  )}
                  <Icon
                    ic={"IcCloseRemove"}
                    onClick={() => {
                      if (queue.length === 0) {
                        cancelAllTask();
                      } else {
                        setCancelAllTaskModalCls(false);
                      }
                    }}
                  />
                </div>
              </div>

              <div
                className={`${classes["upload-list"]} custom-scrollbar`}
                style={{ display: showUploadCon ? "block" : "none" }}>
                {tasks?.map((task, index) => {
                  const {
                    filteredFiles = [],
                    counts: {
                      completedCount,
                      successCount,
                      errorCount,
                      cancelCount,
                      processingCount,
                      pendingCount,
                    },
                  } = getFilteredFiles(
                    task?.files || [],
                    task?.tabFilterStatus,
                  );

                  return (
                    <div className={classes["upload-task"]} key={index}>
                      <div
                        className={`${classes["upload-task-title"]} ${
                          index === showTaskCon
                            ? classes["upload-task-title-sticky"]
                            : ""
                        }`}
                        // style={index == 0 ? { borderTop: "none" } : {}}
                      >
                        {processingCount ? (
                          <>
                            <span
                              style={{
                                cursor: "pointer",
                                textDecoration: !task.tabFilterStatus
                                  ? "underline"
                                  : "none",
                              }}
                              onClick={() => {
                                filterTask(index, null);
                              }}>{`Uploading to ${task?.folderName} (${completedCount} of ${task?.totalFiles})`}</span>
                          </>
                        ) : (
                          <>
                            <span
                              className={`${classes["upload-task-report"]} ${
                                !task.tabFilterStatus ? classes["active"] : ""
                              }`}
                              title={`${task?.folderName} upload: ${
                                successCount
                              } succeeded${
                                cancelCount
                                  ? " | " + cancelCount + " cancelled"
                                  : ""
                              }${
                                errorCount ? " | " + errorCount + " failed" : ""
                              }`}
                              onClick={() => {
                                filterTask(index, null);
                              }}
                              style={{
                                cursor: "pointer",
                              }}>{`${task?.folderName} upload:`}</span>
                          </>
                        )}
                        <div className={classes["upload-task-status"]}>
                          <span
                            onClick={() => {
                              filterTask(index, "succeeded");
                            }}
                            className={`${
                              task.tabFilterStatus === "succeeded"
                                ? classes["active"]
                                : ""
                            }`}>
                            {successCount} succeeded
                          </span>

                          {cancelCount ? (
                            <>
                              {" "}
                              |{" "}
                              <span
                                onClick={() => {
                                  filterTask(index, "cancelled");
                                }}
                                className={`${
                                  task.tabFilterStatus === "cancelled"
                                    ? classes["active"]
                                    : ""
                                }`}>
                                {" "}
                                {cancelCount} cancelled
                              </span>
                            </>
                          ) : null}

                          {errorCount ? (
                            <>
                              {" "}
                              |{" "}
                              <span
                                onClick={() => {
                                  filterTask(index, "failed");
                                }}
                                className={`${
                                  task.tabFilterStatus === "failed"
                                    ? classes["active"]
                                    : ""
                                }`}>
                                {" "}
                                {errorCount} failed{" "}
                              </span>
                            </>
                          ) : null}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}>
                          <Icon
                            ic={
                              index === showTaskCon
                                ? "IcChevronDown"
                                : "IcChevronUp"
                            }
                            onClick={() =>
                              setShowTaskCon(
                                index === showTaskCon ? false : index,
                              )
                            }
                          />
                          {task.tabFilterStatus === "failed" ||
                          task.tabFilterStatus === "cancelled" ? (
                            <Icon
                              onClick={() => {
                                if (task.tabFilterStatus === "failed") {
                                  task?.files?.map((file) => {
                                    if (file.status == "Error")
                                      retryTask(file.id);
                                  });
                                }
                                if (task.tabFilterStatus === "cancelled") {
                                  task?.files?.map((file) => {
                                    if (file.status == "Canceled")
                                      retryTask(file.id);
                                  });
                                }
                              }}
                              ic={"IcGoForward"}
                              kind="background-bold"
                              color="grey-60"
                            />
                          ) : (
                            <Icon
                              ic={"IcCloseRemove"}
                              onClick={() => {
                                if (task.uploadComplete !== task.totalFiles) {
                                  setCancelFolTaskModalCls(false);
                                  setCancelFolTasDetail(task);
                                } else {
                                  setTasks((pre) =>
                                    pre.filter(
                                      (fillTask) =>
                                        fillTask?.folderId != task?.folderId,
                                    ),
                                  );
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div
                        className={classes["upload-task-list"]}
                        style={{
                          display: showTaskCon === index ? "block" : "none",
                        }}>
                        {filteredFiles?.map((file, i) => {
                          if (file.status == "Error") {
                            return (
                              <div
                                className={classes["upload-task-item-error"]}
                                key={i}>
                                <span className={classes["task-img"]}>
                                  <Icon ic={"IcImage"} size="xtra-large" />
                                </span>
                                <span
                                  className={classes["task-file-error"]}
                                  title={file.error}>
                                  {file?.error}
                                </span>
                                <span className={classes["task-status"]}>
                                  <Icon
                                    onClick={() => retryTask(file.id)}
                                    ic={"IcGoForward"}
                                    kind="background-bold"
                                    color="grey-60"
                                  />
                                </span>
                              </div>
                            );
                          }
                          if (file.status == "Canceled") {
                            return (
                              <div
                                className={`${classes["upload-task-item"]} ${
                                  file.status == "Uploading" ||
                                  file.status == "Queued"
                                    ? classes["upload-task-action"]
                                    : ""
                                }`}
                                key={i}>
                                <span className={classes["task-img"]}>
                                  {file?.response?.data
                                    ?.reducedFileSignedPath ? (
                                    // <img
                                    //   src={addParamsURL(
                                    //     decryptUrl(
                                    //       file?.response?.data
                                    //         ?.reducedFileSignedPath
                                    //     ),
                                    //     {
                                    //       size: "s",
                                    //     }
                                    //   )}
                                    //   alt={file.name}
                                    // />
                                    <ImageCard
                                      src={addParamsURL(
                                        decryptUrl(
                                          file?.response?.data
                                            ?.reducedFileSignedPath,
                                        ),
                                        {
                                          size: "a",
                                        },
                                      )}
                                      alt={file.name}
                                      style={{
                                        width: "40px",
                                        height: "40px",
                                        borderRadius: "5px",
                                        display: "block",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <Icon ic={"IcImage"} size="xtra-large" />
                                  )}
                                </span>
                                <span
                                  className={classes["task-file-name"]}
                                  title={file.name}>
                                  {file?.name}
                                </span>
                                <span
                                  className={classes["task-file-size"]}
                                  title={bytesToHumanReadable(file?.size)}>
                                  {bytesToHumanReadable(file?.size)}
                                </span>
                                <span className={classes["task-status"]}>
                                  {file.status == "Canceled" && (
                                    <span className={classes["task-info"]}>
                                      <Icon
                                        onClick={() => retryTask(file.id)}
                                        ic={"IcGoForward"}
                                        kind="background-bold"
                                        color="grey-60"
                                      />
                                    </span>
                                  )}
                                </span>
                              </div>
                            );
                          }
                          if (
                            file.status == "Queued" ||
                            file.status == "Uploading" ||
                            file.status == "Uploaded"
                          ) {
                            return (
                              <div
                                className={`${classes["upload-task-item"]} ${
                                  file.status == "Uploading" ||
                                  file.status == "Queued"
                                    ? classes["upload-task-action"]
                                    : ""
                                }`}
                                key={i}>
                                <span className={classes["task-img"]}>
                                  {file?.response?.data
                                    ?.reducedFileSignedPath ? (
                                    // <img
                                    //   src={addParamsURL(
                                    //     decryptUrl(
                                    //       file?.response?.data
                                    //         ?.reducedFileSignedPath
                                    //     ),
                                    //     {
                                    //       size: "s",
                                    //     }
                                    //   )}
                                    //   alt={file.name}
                                    // />
                                    <ImageCard
                                      src={addParamsURL(
                                        decryptUrl(
                                          file?.response?.data
                                            ?.reducedFileSignedPath,
                                        ),
                                        {
                                          size: "a",
                                        },
                                      )}
                                      alt={file.name}
                                      style={{
                                        width: "40px",
                                        height: "40px",
                                        borderRadius: "5px",
                                        display: "block",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <Icon ic={"IcImage"} size="xtra-large" />
                                  )}
                                </span>
                                <span
                                  className={classes["task-file-name"]}
                                  title={file.name}>
                                  {file?.name}
                                </span>
                                <span
                                  className={classes["task-file-size"]}
                                  title={bytesToHumanReadable(file?.size)}>
                                  {bytesToHumanReadable(file?.size)}
                                </span>
                                <span className={classes["task-status"]}>
                                  {file.status == "Queued" && (
                                    <>
                                      <span className={classes["task-info"]}>
                                        <Icon color="grey-60" ic="IcRadioOff" />
                                      </span>
                                      <span className={classes["task-action"]}>
                                        <Icon
                                          color="grey-60"
                                          ic="IcCloseRemove"
                                          onClick={() => cancelTask(file.id)}
                                        />
                                      </span>
                                    </>
                                  )}
                                  {file.status == "Uploading" && (
                                    <>
                                      <span className={classes["task-info"]}>
                                        <Spinner
                                          labelPosition="right"
                                          size="small"
                                        />
                                        {/* <CircularProgress size={"24px"} /> */}
                                      </span>
                                      <span className={classes["task-action"]}>
                                        <Icon
                                          color="grey-60"
                                          ic="IcCloseRemove"
                                          onClick={() => {
                                            cancelTask(file.id);
                                          }}
                                        />
                                      </span>
                                    </>
                                  )}
                                  {file.status == "Uploaded" && (
                                    <span className={classes["task-info"]}>
                                      <Icon ic={"IcSuccessColored"} />
                                    </span>
                                  )}
                                </span>
                              </div>
                            );
                          }
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <JdsModal
          header={
            <Text
              text="Are you sure you want to cancel the uploading process?"
              appearance="body_m_bold"
            />
          }
          size="m"
          kind="dialog"
          close={cancelAllTaskModalCls}
          onClose={() => {
            setCancelAllTaskModalCls(true);
          }}
          primaryCTA={{
            title: "Yes",
          }}
          secondaryCTA={{
            title: "No",
          }}
          onPrimaryClick={() => {
            cancelAllTask();
            setCancelAllTaskModalCls(true);
          }}
          onSecondaryClick={() => setCancelAllTaskModalCls(true)}></JdsModal>

        {/* Folder task cancel modal */}
        <JdsModal
          header={
            <Text
              text="Are you sure you want to cancel the uploading process?"
              appearance="body_m_bold"
            />
          }
          size="m"
          kind="dialog"
          close={cancelFolTaskModalCls}
          onClose={() => {
            setCancelFolTaskModalCls(true);
          }}
          primaryCTA={{
            title: "Yes",
          }}
          secondaryCTA={{
            title: "No",
          }}
          onPrimaryClick={() => {
            cancelFolderTask(
              cancelFolTasDetail?.folderId,
              cancelFolTasDetail?.eventId,
            );
            dispatch(
              photoUploadTrigger({
                id: cancelFolTasDetail.eventId + cancelFolTasDetail.folderId,
                value: true,
              }),
            );
            setCancelFolTaskModalCls(true);
          }}
          onSecondaryClick={() => setCancelFolTaskModalCls(true)}></JdsModal>
      </NewFileUploadContext.Provider>
    </>
  );
}
